import React, { Fragment, useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Form, FormGroup, Label, Table, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from 'reactstrap';
import { Btn, Spinner } from '../../../../AbstractElements';
import axios from 'axios';
import config from '../../../../config';
import { toast } from 'react-toastify';
import ReactPaginate from 'react-paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';

const OUTCOME_COLOR = { Success: 'success', Partial: 'warning', Failed: 'danger' };
const STATUS_COLOR = { Imported: 'success', Skipped: 'warning', Failed: 'danger' };

// One upload's rows, imported first, then the ones that were skipped or rejected.
const buildDetailRows = (item) => {
    if (!item) return [];
    const imported = (item.imported_rows || []).map(r => ({
        status: 'Imported',
        tenant_code: r.tenant_code,
        store_name: r.store_name,
        month: r.month,
        net_sale: r.net_sale,
        transaction_count: r.transaction_count,
        transactions_created: r.transactions_created,
        reason: ''
    }));
    const notImported = (item.failed_rows || []).map(r => ({
        status: r.status || 'Failed',
        tenant_code: r.tenant_code,
        store_name: r.store_name,
        month: r.month,
        net_sale: null,
        transaction_count: null,
        transactions_created: null,
        reason: r.reason
    }));
    return [...imported, ...notImported];
};

const formatNumber = (value) =>
    value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString();

const BulkImportLogView = () => {

    if (localStorage.getItem("usertype") !== "Backend User") {
        alert('You are not authorized to view this page');
        window.history.back(); // Navigate back to the previous URL
    }

    const concessionaire_id = localStorage.getItem("concessionaireId");
    const login_name = localStorage.getItem("loginName");
    const login_id = localStorage.getItem("loginId");

    const token = localStorage.getItem("sessiontoken");
    const configheader = {
        headers: {
            Authorization: `${token}`
        }
    };

    const today = new Date().toISOString().split('T')[0];

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [status, setStatus] = useState('');
    const [reportData, setReportData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(50);
    const [validationMessage, setValidationMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [tooltip, setTooltip] = useState('');
    const [detailRow, setDetailRow] = useState(null);

    const handleMouseEnter = (text) => setTooltip(text);
    const handleMouseLeave = () => setTooltip('');

    useEffect(() => {
        axios
            .post(`${config.hostname}/users/getuserbyconcessionaire`, { concessionaire_id }, configheader)
            .then((response) => {
                setUsers(response.data);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    const pageCount = Math.ceil(reportData.length / itemsPerPage);
    const currentPageData = reportData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    const resetResults = () => {
        setReportData([]);
        setCurrentPage(0);
        setHasSearched(false);
    };

    const handleStartDateChange = (e) => { setStartDate(e.target.value); resetResults(); };
    const handleEndDateChange = (e) => { setEndDate(e.target.value); resetResults(); };
    const handleUserChange = (e) => { setUserId(e.target.value); resetResults(); };
    const handleStatusChange = (e) => { setStatus(e.target.value); resetResults(); };
    const handlePageClick = (data) => setCurrentPage(data.selected);

    const totals = reportData.reduce((acc, item) => ({
        total_rows: acc.total_rows + (item.total_rows || 0),
        imported: acc.imported + (item.imported || 0),
        skipped: acc.skipped + (item.skipped || 0),
        failed: acc.failed + (item.failed || 0),
        transactions_created: acc.transactions_created + (item.transactions_created || 0)
    }), { total_rows: 0, imported: 0, skipped: 0, failed: 0, transactions_created: 0 });

    const generateReport = (e) => {
        e.preventDefault();

        if (startDate && endDate && endDate < startDate) {
            setValidationMessage('End date cannot be less than start date');
            return;
        }
        setValidationMessage('');

        const data = {
            concessionaire_id,
            start_date: startDate,
            end_date: endDate,
            performed_by: userId,
            status: status
        };

        setLoading(true);
        axios
            .post(`${config.hostname}/salesTransactions/bulkimportlogreport`, data, configheader)
            .then((response) => {
                setReportData(response.data || []);
                setCurrentPage(0);
                setHasSearched(true);
                setLoading(false);
                insertToLog(" generated bulk import log report");
            })
            .catch((error) => {
                toast.error('Error');
                setLoading(false);
                console.log(error);
            });
    };

    const insertToLog = (task) => {
        const data = {
            created_by: login_id,
            concessionaire_id: concessionaire_id,
            module: "Admin",
            task: login_name + task
        }
        axios
            .post(`${config.hostname}/log`, data, configheader)
            .then(() => {
                console.log("inserted to log table")
            })
            .catch((error) => {
                console.log(error);
            });
    }

    const reportColumns = [
        { header: 'Date', key: 'performed_at' },
        { header: 'File', key: 'file_name' },
        { header: 'Uploaded By', key: 'uploaded_by' },
        { header: 'Outcome', key: 'outcome' },
        { header: 'Rows', key: 'total_rows' },
        { header: 'Imported', key: 'imported' },
        { header: 'Skipped', key: 'skipped' },
        { header: 'Failed', key: 'failed' },
        { header: 'Transactions', key: 'transactions_created' }
    ];

    const buildExportRows = () => reportData.map(item => ({
        ...item,
        performed_at: moment(item.performed_at).format('DD/MM/YYYY HH:mm:ss')
    }));

    const buildMetadata = () => {
        const selectedUser = users.find(u => u._id === userId);
        return [
            ['From', startDate || 'Beginning', 'To', endDate || 'Today'],
            ['Uploaded By', selectedUser ? selectedUser.userFirstName : 'All'],
            ['Outcome', status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'],
            ['Total Uploads', String(reportData.length)]
        ];
    };

    const totalsRowValues = () => reportColumns.map(column => {
        if (Object.prototype.hasOwnProperty.call(totals, column.key)) return totals[column.key];
        if (column.key === 'performed_at') return 'Total';
        return '';
    });

    const handleExport = (format) => {
        if (reportData.length === 0) {
            toast.warning('Generate the report before exporting');
            return;
        }
        if (format === 'excel') {
            exportToExcel("Bulk Import Log Report", buildMetadata(), reportColumns, buildExportRows(), "BulkImportLogReport.xlsx");
            insertToLog(" exported bulk import log report to excel");
        } else if (format === 'pdf') {
            exportToPDF("Bulk Import Log Report", buildMetadata(), reportColumns, buildExportRows(), "BulkImportLogReport.pdf");
            insertToLog(" exported bulk import log report to pdf");
        }
    };

    const exportToExcel = (reportHeading, metadata, columns, rows, fileName) => {
        const exportData = rows.map(item => {
            const formattedItem = {};
            columns.forEach(column => { formattedItem[column.header] = item[column.key]; });
            return formattedItem;
        });

        const totalsRow = {};
        const totalValues = totalsRowValues();
        columns.forEach((column, index) => { totalsRow[column.header] = totalValues[index]; });
        exportData.push(totalsRow);

        const worksheet = XLSX.utils.aoa_to_sheet([[]]);
        XLSX.utils.sheet_add_aoa(worksheet, [[reportHeading]], { origin: 'A1' });
        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }];

        const metadataArray = metadata.map(item => [Array.isArray(item) ? item.join(': ') : item]);
        XLSX.utils.sheet_add_aoa(worksheet, metadataArray, { origin: 'A2' });

        XLSX.utils.sheet_add_aoa(worksheet, [columns.map(column => column.header)], { origin: `A${metadata.length + 2}` });
        XLSX.utils.sheet_add_json(worksheet, exportData, { origin: `A${metadata.length + 3}`, skipHeader: true });

        worksheet['!cols'] = columns.map(column => {
            const maxLength = Math.max(
                ...exportData.map(item => (item[column.header] !== undefined && item[column.header] !== null ? item[column.header].toString().length : 0)),
                column.header.length
            );
            return { wch: maxLength + 2 };
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Second sheet: every individual row across every upload in the report, imported or not
        const detailRows = [];
        reportData.forEach(item => {
            buildDetailRows(item).forEach(dr => {
                detailRows.push({
                    'Upload Date': moment(item.performed_at).format('DD/MM/YYYY HH:mm:ss'),
                    'File': item.file_name,
                    'Status': dr.status,
                    'Tenant Code': dr.tenant_code,
                    'Store Name': dr.store_name,
                    'Month': dr.month,
                    'Net Sale': dr.net_sale === null ? '' : dr.net_sale,
                    'Txn Count': dr.transaction_count === null ? '' : dr.transaction_count,
                    'Days': dr.transactions_created === null ? '' : dr.transactions_created,
                    'Reason': dr.reason
                });
            });
        });
        if (detailRows.length > 0) {
            const detailSheet = XLSX.utils.json_to_sheet(detailRows);
            detailSheet['!cols'] = [{ wch: 20 }, { wch: 26 }, { wch: 10 }, { wch: 14 }, { wch: 24 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 60 }];
            XLSX.utils.book_append_sheet(workbook, detailSheet, "Row Details");
        }

        XLSX.writeFile(workbook, fileName);
    };

    const exportToPDF = (reportHeading, metadata, columns, rows, fileName) => {
        const doc = new jsPDF({ orientation: 'landscape' });

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(reportHeading, doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');

        const lineHeight = 5;
        const startX = 10;
        let startY = 20;
        metadata.forEach((item, index) => {
            doc.text(Array.isArray(item) ? item.join(': ') : item, startX, startY + (index * lineHeight));
        });
        startY += metadata.length * lineHeight + 5;

        const tableHeaders = columns.map(column => column.header);
        const tableRows = rows.map(item => columns.map(column => {
            const value = item[column.key];
            return value === undefined || value === null ? '' : String(value);
        }));
        const totalsRow = totalsRowValues().map(v => String(v));

        doc.autoTable({
            startY: startY,
            head: [tableHeaders],
            body: tableRows,
            foot: [totalsRow],
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fontSize: 7 },
            footStyles: { fontSize: 7 },
            margin: { top: 10, right: 10, bottom: 10, left: 10 }
        });

        doc.save(fileName);
    };

    return (
        <Fragment>
            <Form className='theme-form' onSubmit={generateReport}>
                <Card>
                    <CardBody>
                        <Row>
                            <Col md="3">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="startdate">Start Date</Label>
                                    <input className="form-control digits"
                                        id="startdate" type="date" name="startdate" value={startDate}
                                        max={today} onChange={handleStartDateChange} />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="enddate">End Date</Label>
                                    <input className="form-control digits"
                                        id="enddate" type="date" name="enddate" value={endDate}
                                        max={today} onChange={handleEndDateChange} />
                                </FormGroup>
                                {validationMessage && (
                                    <span className="text-danger">{validationMessage}</span>
                                )}
                            </Col>
                            <Col md="3">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="uploaded_by">Uploaded By</Label>
                                    <select name="uploaded_by" id="uploaded_by" className="form-control"
                                        value={userId} onChange={handleUserChange}>
                                        <option value="">All</option>
                                        {users.map(user => (
                                            <option key={user._id} value={user._id}>{user.userFirstName}</option>
                                        ))}
                                    </select>
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="status">Outcome</Label>
                                    <select name="status" id="status" className="form-control"
                                        value={status} onChange={handleStatusChange}>
                                        <option value="">All</option>
                                        <option value="success">Success - everything imported</option>
                                        <option value="partial">Partial - some rows not imported</option>
                                        <option value="failed">Failed - nothing imported</option>
                                    </select>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm="3">
                                <Btn attrBtn={{ color: 'primary', className: 'me-3', type: "submit" }}>Generate Report</Btn>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>

            {loading === true ? (
                <div className="loader-box">
                    <Spinner attrSpinner={{ className: 'loader-3' }} />
                </div>
            ) : (
                <Card>
                    <CardHeader>
                        <Row className="justify-content-end">
                            <Col xs="auto" style={{ position: 'relative' }}>
                                <Btn
                                    attrBtn={{
                                        color: 'primary',
                                        type: 'button',
                                        onClick: () => handleExport('excel'),
                                        onMouseEnter: () => handleMouseEnter('Export to Excel'),
                                        onMouseLeave: handleMouseLeave
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} size="lg" />
                                </Btn>
                                {tooltip === 'Export to Excel' && (
                                    <div style={{
                                        position: 'absolute', top: '-30px', left: '50%', transform: 'translateX(-50%)',
                                        backgroundColor: 'black', color: 'white', padding: '5px 10px', borderRadius: '3px',
                                        whiteSpace: 'nowrap', fontSize: '12px', zIndex: 1
                                    }}>
                                        {tooltip}
                                    </div>
                                )}
                            </Col>
                            <Col xs="auto" style={{ position: 'relative' }}>
                                <Btn
                                    attrBtn={{
                                        color: 'primary',
                                        type: 'button',
                                        onClick: () => handleExport('pdf'),
                                        onMouseEnter: () => handleMouseEnter('Export to PDF'),
                                        onMouseLeave: handleMouseLeave
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} size="lg" />
                                </Btn>
                                {tooltip === 'Export to PDF' && (
                                    <div style={{
                                        position: 'absolute', top: '-30px', left: '50%', transform: 'translateX(-50%)',
                                        backgroundColor: 'black', color: 'white', padding: '5px 10px', borderRadius: '3px',
                                        whiteSpace: 'nowrap', fontSize: '12px', zIndex: 1
                                    }}>
                                        {tooltip}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Col sm="12" lg="12" xl="12">
                            <div className="table-responsive">
                                <Table>
                                    <thead>
                                        <tr>
                                            <th scope="col">Date</th>
                                            <th scope="col">File</th>
                                            <th scope="col">Uploaded By</th>
                                            <th scope="col">Outcome</th>
                                            <th scope="col" className="text-center">Rows</th>
                                            <th scope="col" className="text-center">Imported</th>
                                            <th scope="col" className="text-center">Skipped</th>
                                            <th scope="col" className="text-center">Failed</th>
                                            <th scope="col" className="text-center">Transactions</th>
                                            <th scope="col"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPageData.map((item) => (
                                            <tr key={item._id}>
                                                <td>{moment(item.performed_at).format('DD/MM/YYYY HH:mm:ss')}</td>
                                                <td>{item.file_name || '-'}</td>
                                                <td>{item.uploaded_by || '-'}</td>
                                                <td>
                                                    <Badge color={OUTCOME_COLOR[item.outcome] || 'secondary'}>
                                                        {item.outcome}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">{item.total_rows}</td>
                                                <td className="text-center txt-success fw-bold">{item.imported}</td>
                                                <td className="text-center txt-warning fw-bold">{item.skipped}</td>
                                                <td className="text-center txt-danger fw-bold">{item.failed}</td>
                                                <td className="text-center">{item.transactions_created}</td>
                                                <td>
                                                    <Btn attrBtn={{
                                                        color: 'link',
                                                        className: 'p-0',
                                                        type: 'button',
                                                        onClick: () => setDetailRow(item)
                                                    }}>
                                                        View Details
                                                    </Btn>
                                                </td>
                                            </tr>
                                        ))}
                                        {reportData.length > 0 && (
                                            <tr className="fw-bold">
                                                <td colSpan="4">Total</td>
                                                <td className="text-center">{totals.total_rows}</td>
                                                <td className="text-center">{totals.imported}</td>
                                                <td className="text-center">{totals.skipped}</td>
                                                <td className="text-center">{totals.failed}</td>
                                                <td className="text-center">{totals.transactions_created}</td>
                                                <td></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {reportData.length === 0 && (
                                <p className="mb-0 mt-2">
                                    {hasSearched
                                        ? 'No bulk imports match the selected filters.'
                                        : 'Choose your filters and click Generate Report.'}
                                </p>
                            )}

                            {reportData.length > itemsPerPage && (
                                <ReactPaginate
                                    previousLabel={'Previous'}
                                    nextLabel={'Next'}
                                    breakLabel={'...'}
                                    breakClassName={'break-me'}
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName={'pagination'}
                                    subContainerClassName={'pages pagination'}
                                    activeClassName={'active'}
                                />
                            )}
                        </Col>
                    </CardBody>
                </Card>
            )}

            <Modal
                className="modal-lg modal-dialog-centered"
                isOpen={detailRow !== null}
                toggle={() => setDetailRow(null)}
            >
                <ModalHeader toggle={() => setDetailRow(null)}>
                    Upload Details
                </ModalHeader>
                <ModalBody>
                    {detailRow && (
                        <>
                            <p className="text-muted mb-2">
                                {detailRow.file_name || 'Upload'} · {moment(detailRow.performed_at).format('DD/MM/YYYY HH:mm:ss')} · by {detailRow.uploaded_by || '-'}
                            </p>
                            <p className="mb-3">
                                <Badge color="success" className="me-2">Imported {detailRow.imported}</Badge>
                                <Badge color="warning" className="me-2">Skipped {detailRow.skipped}</Badge>
                                <Badge color="danger" className="me-2">Failed {detailRow.failed}</Badge>
                                <span className="text-muted">{detailRow.transactions_created} sales transactions created</span>
                            </p>
                            <div className="table-responsive">
                                <Table>
                                    <thead>
                                        <tr>
                                            <th scope="col">Status</th>
                                            <th scope="col">Tenant Code</th>
                                            <th scope="col">Store Name</th>
                                            <th scope="col">Month</th>
                                            <th scope="col" className="text-end">Net Sale</th>
                                            <th scope="col" className="text-end">Txn Count</th>
                                            <th scope="col" className="text-center">Days</th>
                                            <th scope="col">Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {buildDetailRows(detailRow).map((dr, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <Badge color={STATUS_COLOR[dr.status] || 'secondary'}>
                                                        {dr.status}
                                                    </Badge>
                                                </td>
                                                <td>{dr.tenant_code || '-'}</td>
                                                <td>{dr.store_name || '-'}</td>
                                                <td>{dr.month || '-'}</td>
                                                <td className="text-end">{formatNumber(dr.net_sale)}</td>
                                                <td className="text-end">{formatNumber(dr.transaction_count)}</td>
                                                <td className="text-center">{formatNumber(dr.transactions_created)}</td>
                                                <td className={dr.status === 'Imported' ? '' : 'txt-danger'}>{dr.reason || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            {buildDetailRows(detailRow).length === 0 && (
                                <p className="mb-0">No row details were recorded for this upload.</p>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'primary', onClick: () => setDetailRow(null) }}>Close</Btn>
                </ModalFooter>
            </Modal>
        </Fragment>
    )
}

export default BulkImportLogView
